import { createSelector } from 'reselect';

import { TABS } from 'constants/Layout';

import { MODE } from 'modules/entity/constants';
import { getEntity } from 'modules/entity/selectors';
import { getPendingAdvices } from 'modules/works/selectors/advices';
import { getSelectedChecklistGroupId } from 'modules/works/selectors';
import { INTERVENTION_STATUS, CHECKLIST_GROUP_STATUS } from 'modules/entity/subEntities/intervention/constants';

import { getUI } from '.';
import { isPaymentSuccess } from '../utils';

const getOpinion = createSelector(getUI, ui => ui.opinion);
const getCallMeBack = createSelector(getUI, ui => ui.callMeBack);
const getDownloadMedias = createSelector(getUI, ui => ui.downloadMedias);
const getPdfOffer = createSelector(getUI, ui => ui.pdfOffer);
const getContactForm = createSelector(getUI, ui => ui.contactForm);

export const isLoadingOpinion = createSelector(getOpinion, opinion => opinion.isLoading);
export const isLoadingCallMeBack = createSelector(getCallMeBack, callMeBack => callMeBack.isLoading);
export const isLoadingDownloadMedias = createSelector(getDownloadMedias, downloadMedias => downloadMedias.isLoading);
export const isLoadingPdfOffer = createSelector(getPdfOffer, pdfOffer => pdfOffer.isLoading);
export const isLoadingContactForm = createSelector(getContactForm, contactForm => contactForm.isLoading);
export const isContactFormSent = createSelector(getContactForm, contactForm => contactForm.isSent);

export const getMainContent = createSelector(
  getEntity,
  getPendingAdvices,
  (intervention, pendingAdvices) => {
    const { answerUrl, payOnlineUrl, payDepositUrl, payments } = intervention;
    const CONFIG = {
      [MODE.TECHNICIAN]: TABS.WORKS_TAB,
      [MODE.SALES]: TABS.OFFERS_TAB,
      [MODE.PARTS]: TABS.PARTS_TAB,
    };

    /**
     * We want to show the payment tab :
     * - If we have an `answerUrl` (deposit) or `payOnlineUrl` (payment)
     * - If we have already paid at least once (`payments` array)
     */

    const hasPending = pendingAdvices.length > 0;
    const hasAnswerUrl = Boolean(answerUrl);
    const hasPayUrl = Boolean(payOnlineUrl || payDepositUrl);
    const hasUrl = hasAnswerUrl || hasPayUrl;
    const hasPaid = payments?.some((payment) => isPaymentSuccess(payment));
    const showPaymentTab = hasUrl ? hasPayUrl : hasPaid;

    if (showPaymentTab && !hasPending) {
      return TABS.PAYMENT_TAB;
    }

    return intervention.mode ? CONFIG[intervention.mode] : undefined;
  }
);

export const isLayoutWorks = createSelector(getMainContent, content => content === TABS.WORKS_TAB);

export const getVAT = createSelector(getEntity, ({ valueAddedTax }) => valueAddedTax);
export const hasFinalInvoice = createSelector(
  getEntity,
  ({ finalInvoice = {} }) => Object.keys(finalInvoice).length > 0
);
export const hasAnsweredStatus = createSelector(
  getEntity,
  getSelectedChecklistGroupId,
  ({ status, checklistGroups }, selectedChecklistGroupId) =>
    status === INTERVENTION_STATUS.ANSWERED &&
    (!checklistGroups ||
      checklistGroups.some(
        ({ id, status: checklistGroupStatus }) =>
          id === selectedChecklistGroupId && checklistGroupStatus === CHECKLIST_GROUP_STATUS.ACCEPTED
      ))
);

export const isToBeSent = createSelector(getEntity, ({ status }) => status === INTERVENTION_STATUS.TO_BE_SENT);
